import type { CrewlinkClient } from '../supabase-client';
import type {
  MyDayHistoryRow,
  MyDayItemRow,
  MyDayItemState,
  MyDayPriority,
  MyDayRecurRule,
} from '../database.types';

/**
 * Writer wrappers for My Day — M12 / S12-01, migration 0028.
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. This is the wrapper layer. UI code never calls
 * `.rpc(...)` directly and never calls `.insert(...)` at all — and here it
 * could not: `authenticated` holds SELECT on `my_day_item` and nothing else.
 *
 * Identity is taken from `auth.uid()` inside each function; it is never passed
 * from here (INV-T23).
 */

/** What the screen collected. **`time` absent means day-only, deliberately.** */
export interface NewMyDayItem {
  readonly title: string;
  readonly description: string | null;
  /** `yyyy-mm-dd`, in the canonical Crewlink timezone's calendar. */
  readonly dueDate: string;
  /**
   * `HH:mm`, or null.
   *
   * **NULL IS A REAL ANSWER, NOT A MISSING ONE.** A follow-up with no time is a
   * thing to do that day; Crewlink never fills in 8am, 9am or noon for it.
   */
  readonly dueTime: string | null;
  /**
   * **THE CANONICAL CREWLINK TIMEZONE — never the device's** (the S12-01 review
   * blocker). Required with a time, ignored without one.
   *
   * A phone in Manila and a Crewlink set to New York must produce *"9am New
   * York"*, because "9am" is a promise about his working day, not about where
   * his handset happens to be. It is stored beside the instant so the record
   * still knows what he meant after a later Settings change.
   */
  readonly timezone: string | null;
  readonly projectId: string | null;
  /** When present, **the server reads its Project and ignores `projectId`.** */
  readonly captureId: string | null;
  /** normal or high (0030, D-050). **Null means normal**, said by the server. */
  readonly priority: MyDayPriority | null;
  /** A repeat from the CLOSED vocabulary (0030, D-049), or null for one-time. */
  readonly recurRule: MyDayRecurRule | null;
  /** Last day the repeat may land on, or null for no end. Rule-less: null. */
  readonly recurEndsOn: string | null;
}

export async function createMyDayItem(
  client: CrewlinkClient,
  item: NewMyDayItem,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('create_my_day_item', {
    p_title: item.title,
    p_description: item.description,
    p_due_date: item.dueDate,
    p_due_time: item.dueTime,
    p_timezone: item.timezone,
    p_project_id: item.projectId,
    p_capture_id: item.captureId,
    p_priority: item.priority,
    p_recur_rule: item.recurRule,
    p_recur_ends_on: item.recurEndsOn,
  });

  if (error) throw error;
  if (!data) throw new Error('create_my_day_item returned no row');
  return data;
}

/**
 * Done. **Not a delete** — which is what makes an honest Undo possible.
 *
 * `expected` is the state the screen believed the item was in. The database
 * writes only while that is still true, so **a stale Done cannot re-stamp an
 * item somebody already reopened**, and the caller is told rather than winning
 * silently.
 */
export async function completeMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expected: MyDayItemState = 'open',
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('complete_my_day_item', {
    p_item_id: itemId,
    p_expected_state: expected,
  });
  if (error) throw error;
  if (!data) throw new Error('complete_my_day_item returned no row');
  return data;
}

/**
 * Undo, under the same guard: **a stale Undo cannot reopen an item that was
 * legitimately completed since.**
 */
export async function reopenMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expected: MyDayItemState = 'done',
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('reopen_my_day_item', {
    p_item_id: itemId,
    p_expected_state: expected,
  });
  if (error) throw error;
  if (!data) throw new Error('reopen_my_day_item returned no row');
  return data;
}

/**
 * What went wrong, in a sentence Brandon can act on.
 *
 * **THE CODES COME FROM THE WRITER, NOT FROM PARSING PROSE.** Only strings are
 * read, so a code arriving in some other shape is ignored rather than
 * stringified and searched.
 */
export function describeMyDayFailure(error: unknown): string {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const raw = error as { code?: unknown; message?: unknown } | null;
  const detail = `${text(raw?.code)} ${text(raw?.message)}`;

  if (detail.includes('CL040')) return 'Give the follow-up a shorter title, or add one.';
  if (detail.includes('CL041')) return 'Choose a date for the follow-up.';
  if (detail.includes('CL042')) return 'Crewlink could not use that time zone for the time.';
  if (detail.includes('CL043')) {
    return 'This follow-up repeats — choose Skip this one or End the series.';
  }
  if (detail.includes('CL044')) return 'A snooze has to end in the future.';
  if (detail.includes('CL045')) return 'Crewlink could not use that repeat.';
  if (detail.includes('CL046')) return 'That follow-up does not repeat.';
  if (detail.includes('CL047')) {
    return 'That could not be undone any more — the follow-up has moved on.';
  }
  if (detail.includes('CL048')) return 'Crewlink could not use that priority.';
  if (detail.includes('CL049')) {
    return 'That repeat has no days left — its end date has passed.';
  }
  if (detail.includes('CL050')) return 'That repeat is already running.';
  if (detail.includes('CL006')) {
    return 'Crewlink needs your time zone in Settings before it can work out your day.';
  }
  if (detail.includes('55000')) {
    return 'That follow-up changed somewhere else. Crewlink reloaded it — check it and try again.';
  }
  if (detail.includes('P0002')) return 'Crewlink could not find that record any more.';
  return 'Crewlink could not save that. Check your connection and try again.';
}

/**
 * Edit — **what it says, never when it is due** (S12-02).
 *
 * `expectedUpdatedAt` is the `updated_at` the screen was showing. The write
 * lands only while that is still current, so **an edit decided against a list
 * from ten minutes ago cannot overwrite a newer title**.
 *
 * There is no schedule argument, and there is none on the server either:
 * fixing a typo must not be able to move a reminder. Rescheduling is
 * `rescheduleMyDayItem`, which is a deliberate separate act.
 */
export async function updateMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
  fields: {
    readonly title: string;
    readonly description: string | null;
    readonly projectId: string | null;
    /** **Null means "leave it"** — an edit silent on priority never downgrades. */
    readonly priority: MyDayPriority | null;
  },
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('update_my_day_item', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
    p_title: fields.title,
    p_description: fields.description,
    p_project_id: fields.projectId,
    p_priority: fields.priority,
  });
  if (error) throw error;
  if (!data) throw new Error('update_my_day_item returned no row');
  return data;
}

/**
 * Reschedule — **the only way a schedule is ever re-authored** (S12-02).
 *
 * `dueTime` null means day-only, and the server clears the time, the zone and
 * the instant with it. **No timezone travels from here**: the server reads the
 * canonical saved zone itself, which is what makes it impossible for a device's
 * idea of "9am" to reach a stored reminder.
 */
export async function rescheduleMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
  when: { readonly dueDate: string; readonly dueTime: string | null },
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('reschedule_my_day_item', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
    p_due_date: when.dueDate,
    p_due_time: when.dueTime,
  });
  if (error) throw error;
  if (!data) throw new Error('reschedule_my_day_item returned no row');
  return data;
}

/**
 * Dismiss — *"I no longer need this"*, which is **not** *"I did this"*.
 *
 * **Not a delete**, which is what makes the same Undo possible: `completed_at`
 * stays null, because a dismissed follow-up was never completed and stamping it
 * would be a small permanent lie.
 */
export async function dismissMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expected: MyDayItemState = 'open',
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('dismiss_my_day_item', {
    p_item_id: itemId,
    p_expected_state: expected,
  });
  if (error) throw error;
  if (!data) throw new Error('dismiss_my_day_item returned no row');
  return data;
}

/**
 * Snooze — rest an open follow-up until a future instant (WD-1, D-050).
 *
 * **THE AUTHORED SCHEDULE DOES NOT TRAVEL AND CANNOT MOVE.** The server's SET
 * clause holds `snoozed_until` alone, so when the snooze lapses the untouched
 * schedule puts the item exactly where it always belonged — including Overdue.
 */
export async function snoozeMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
  snoozedUntil: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('snooze_my_day_item', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
    p_snoozed_until: snoozedUntil,
  });
  if (error) throw error;
  if (!data) throw new Error('snooze_my_day_item returned no row');
  return data;
}

/** Wake it now. The true bucket returns immediately — nothing to recompute. */
export async function unsnoozeMyDayItem(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('unsnooze_my_day_item', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
  });
  if (error) throw error;
  if (!data) throw new Error('unsnooze_my_day_item returned no row');
  return data;
}

/**
 * Turn a repeat on, change it, or turn it off (WD-1, D-049).
 *
 * **THE ANCHOR IS THE SERVER'S** — the item's current due date, never an
 * argument from here — and turning a repeat off keeps the item open on its
 * current schedule: removing the repeat is not removing the follow-up.
 */
export async function setMyDayRecurrence(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
  repeat: { readonly rule: MyDayRecurRule | null; readonly endsOn: string | null },
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('set_my_day_recurrence', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
    p_recur_rule: repeat.rule,
    p_recur_ends_on: repeat.endsOn,
  });
  if (error) throw error;
  if (!data) throw new Error('set_my_day_recurrence returned no row');
  return data;
}

/** Attach a Capture. **The Capture decides the Project** — 0028's rule, kept. */
export async function linkMyDayCapture(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
  captureId: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('link_my_day_capture', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
    p_capture_id: captureId,
  });
  if (error) throw error;
  if (!data) throw new Error('link_my_day_capture returned no row');
  return data;
}

/** Detach the Capture. **The Project stays** (D-051) — evidence, not identity. */
export async function unlinkMyDayCapture(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('unlink_my_day_capture', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
  });
  if (error) throw error;
  if (!data) throw new Error('unlink_my_day_capture returned no row');
  return data;
}

/**
 * *"Skip this one"* — one of the two explicit meanings a recurring dismissal
 * must be resolved into (D-049; plain Dismiss on a repeat refuses with CL043).
 * The series continues on its own cadence.
 */
export async function skipMyDayOccurrence(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('skip_my_day_occurrence', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
  });
  if (error) throw error;
  if (!data) throw new Error('skip_my_day_occurrence returned no row');
  return data;
}

/**
 * *"End the series"* — the other meaning. No future occurrence is advanced to;
 * the row keeps its repeat metadata, and Reopen revives the series.
 */
export async function endMyDaySeries(
  client: CrewlinkClient,
  itemId: string,
  expected: MyDayItemState = 'open',
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('end_my_day_series', {
    p_item_id: itemId,
    p_expected_state: expected,
  });
  if (error) throw error;
  if (!data) throw new Error('end_my_day_series returned no row');
  return data;
}

/**
 * *"Pick this repeat back up"* — the History recovery for an ENDED recurring
 * series (M2), and **deliberately not the same verb as Reopen.**
 *
 * Reopen restores an item to where it was. For a repeat ended months ago that
 * would hand back an occurrence long past — an instantly-overdue ghost. Resume
 * keeps the authored rule and anchor exactly as stored and picks up on the
 * first occurrence of that same grid still ahead. **The period the series was
 * off is not missed work**, so no skipped occurrences are recorded for it.
 */
export async function resumeMyDaySeries(
  client: CrewlinkClient,
  itemId: string,
  expected: MyDayItemState,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('resume_my_day_series', {
    p_item_id: itemId,
    p_expected_state: expected,
  });
  if (error) throw error;
  if (!data) throw new Error('resume_my_day_series returned no row');
  return data;
}

/**
 * Undo an advance: the occurrence just completed or skipped comes back,
 * restored from the event's own snapshot — append-only, so the History keeps
 * both the advance and the change of mind. The server refuses (CL047) when
 * anything happened since; a past it cannot prove is not restored.
 */
export async function undoMyDayOccurrence(
  client: CrewlinkClient,
  itemId: string,
  expectedUpdatedAt: string,
): Promise<MyDayItemRow> {
  const { data, error } = await client.rpc('undo_my_day_occurrence', {
    p_item_id: itemId,
    p_expected_updated_at: expectedUpdatedAt,
  });
  if (error) throw error;
  if (!data) throw new Error('undo_my_day_occurrence returned no row');
  return data;
}

/** The last 100 History events, newest first — **the server's bound and order.** */
export async function listMyDayHistory(client: CrewlinkClient): Promise<MyDayHistoryRow[]> {
  const { data, error } = await client.rpc('list_my_day_history', {});
  if (error) throw error;
  return data ?? [];
}

/**
 * Was this the STALE-STATE refusal specifically?
 *
 * It is the one failure where the SCREEN is now known to be out of date — the
 * item moved somewhere else — so the caller must re-read before showing
 * anything further. Every other refusal leaves the list exactly as it was.
 */
export function isStaleMyDayWrite(error: unknown): boolean {
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const raw = error as { code?: unknown; message?: unknown } | null;
  return `${text(raw?.code)} ${text(raw?.message)}`.includes('55000');
}
